import { useState, useCallback } from 'react'
import { FormattedMessage, FormattedDate, useIntl } from 'react-intl'
import { useSnackbar } from 'notistack'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import Add from '@mui/icons-material/Add'
import Upload from '@mui/icons-material/Upload'
import Edit from '@mui/icons-material/Edit'
import Delete from '@mui/icons-material/Delete'
import Download from '@mui/icons-material/Download'
import { isEmpty, get } from 'lodash'
import FormDrawer from '../../../../shared/components/drawers/FormDrawer'
import ProjectFileForm from './ProjectFileForm'
import { useConfirmationDialog } from '../../../../system/ConfirmationDialogProvider'
import formatBytes from '../../../../shared/utils/format/formatBytes'
import Flex from '../../../../shared/components/layouts/Flex'

const getFileTypeColor = (fileType) => {
  switch (fileType) {
    case 'text':
      return 'primary'
    case 'binary':
      return 'secondary'
    default:
      return 'default'
  }
}

function ProjectFilesTab({
  files,
  loading,
  createFile,
  updateFile,
  destroyFile,
  editFile,
  onSuccess
}) {
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const { openConfirmationDialog } = useConfirmationDialog()
  const [drawer, setDrawer] = useState({ open: false, file: null, mode: 'text' })

  const handleOpenDrawer = useCallback((mode, file = null) => {
    if (file) {
      editFile(file.id)
        .then((response) => {
          setDrawer({ open: true, file: response.projectFile, mode: 'text' })
        })
        .catch(() => {
          enqueueSnackbar(formatMessage({ id: 'projectFiles.edit.error' }), { variant: 'error' })
        })
    } else {
      setDrawer({ open: true, file: null, mode })
    }
  }, [editFile, enqueueSnackbar, formatMessage])

  const handleCloseDrawer = useCallback(() => {
    setDrawer({ open: false, file: null, mode: 'text' })
  }, [])

  const handleSubmit = useCallback((values) => {
    const fileData = { projectFile: values }

    if (drawer.file) {
      return updateFile(drawer.file.id, fileData)
        .then(() => {
          handleCloseDrawer()
          onSuccess()
        })
    }

    return createFile(fileData)
      .then(() => {
        handleCloseDrawer()
        onSuccess()
      })
  }, [drawer.file, createFile, updateFile, handleCloseDrawer, onSuccess])

  const handleDelete = useCallback((file) => {
    openConfirmationDialog({
      title: <FormattedMessage id="projectFiles.delete.title" />,
      message: <FormattedMessage id="projectFiles.delete.message" values={{ name: file.fileName }} />,
      onConfirm: () => destroyFile(file.id)
        .then(() => {
          onSuccess()
        })
        .catch(() => {
          enqueueSnackbar(formatMessage({ id: 'projectFiles.delete.error' }), { variant: 'error' })
        }),
      onCancel: () => Promise.resolve(),
    })
  }, [openConfirmationDialog, destroyFile, onSuccess, enqueueSnackbar, formatMessage])

  const getDrawerTitle = () => {
    if (drawer.file) {
      return 'projectFiles.edit.title'
    }
    return drawer.mode === 'text' ? 'projectFiles.create.title' : 'projectFiles.createBinary.title'
  }

  return (
    <>
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          <FormattedMessage id="dockerProjects.sections.files" />
        </Typography>

        <Flex gap={1} sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDrawer('text')}
          >
            <FormattedMessage id="projectFiles.actions.createText" />
          </Button>
          <Button
            variant="outlined"
            startIcon={<Upload />}
            onClick={() => handleOpenDrawer('binary')}
          >
            <FormattedMessage id="projectFiles.actions.uploadBinary" />
          </Button>
        </Flex>

        {isEmpty(files) ? (
          <Typography color="text.secondary">
            <FormattedMessage id="projectFiles.empty" />
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <FormattedMessage id="projectFiles.index.columns.name" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="projectFiles.index.columns.type" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="projectFiles.index.columns.size" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="projectFiles.index.columns.createdAt" />
                  </TableCell>
                  <TableCell align="right">
                    <FormattedMessage id="projectFiles.index.columns.actions" />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((file) => (
                  <TableRow key={file.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {get(file, 'fileName', get(file, 'name', '-'))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={<FormattedMessage id={`projectFiles.fileTypes.${get(file, 'fileType', 'text')}`} />}
                        color={getFileTypeColor(get(file, 'fileType'))}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatBytes(get(file, 'fileSize', get(file, 'size')))}</TableCell>
                    <TableCell>
                      {file.createdAt ? (
                        <FormattedDate
                          value={file.createdAt}
                          year="numeric"
                          month="short"
                          day="numeric"
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {get(file, 'fileType') === 'text' && (
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDrawer('text', file)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      )}
                      {file.downloadUrl && (
                        <IconButton
                          size="small"
                          component={Link}
                          href={file.downloadUrl}
                          download
                        >
                          <Download fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(file)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <FormDrawer
        open={drawer.open}
        onClose={handleCloseDrawer}
        titleId={getDrawerTitle()}
      >
        <ProjectFileForm
          file={drawer.file}
          mode={drawer.mode}
          onSubmit={handleSubmit}
          onCancel={handleCloseDrawer}
        />
      </FormDrawer>
    </>
  )
}

export default ProjectFilesTab
